import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useFeedbackTypeApi } from '../../services/api/useFeedbackType.api';

interface SendErrorReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (data: { title: string; feedbackTypeId: number; additionalComments: string }) => void;
  isSending: boolean;
}

function SendErrorReportDialog({ open, onClose, onSend, isSending }: SendErrorReportDialogProps) {
  const { getFeedbackTypes } = useFeedbackTypeApi();
  const { data: feedbackTypes } = useQuery({
    queryKey: ['feedbackTypes'],
    queryFn: getFeedbackTypes,
  });

  const [title, setTitle] = useState('');
  const [feedbackTypeId, setFeedbackTypeId] = useState<number | ''>('');
  const [additionalComments, setAdditionalComments] = useState('');

  const description =
    'No comparison data is currently available for the selected group. This situation can occur when the applied filters are too restrictive and exclude relevant records from the dataset. It may also happen when there are very few observations for the selected comparison group, limiting meaningful analysis. As a result, the system is unable to generate reliable comparative insights at this time. Consider adjusting the filters or selecting a different group to view available comparisons.';

  const isFormValid = title.trim().length > 0 && feedbackTypeId !== '';

  const handleSend = () => {
    if (!isFormValid) return;

    onSend({ title, feedbackTypeId: feedbackTypeId as number, additionalComments });
    setTitle('');
    setFeedbackTypeId('');
    setAdditionalComments('');
  };

  const handleClose = () => {
    setTitle('');
    setFeedbackTypeId('');
    setAdditionalComments('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      maxWidth='sm'
      fullWidth
    >
      <DialogContent sx={{ padding: '24px' }}>
        <Typography variant='h6' sx={{ fontWeight: 700, marginBottom: '20px' }}>
          Send Error Report
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
            Title
          </Typography>
          <TextField
            fullWidth
            placeholder='Enter feedback title'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            size='small'
            sx={{ marginBottom: 2 }}
          />

          <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
            Feedback type
          </Typography>
          <Select
            value={feedbackTypeId}
            onChange={(e) => setFeedbackTypeId(e.target.value as number)}
            size='small'
            displayEmpty
            sx={{ marginBottom: 2 }}
          >
            <MenuItem value='' disabled>
              Select feedback type
            </MenuItem>
            {feedbackTypes?.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>

          <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
            Description
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            size='small'
            sx={{ marginBottom: 2 }}
            value={description}
            slotProps={{
              input: {
                readOnly: true,
                sx: {
                  backgroundColor: '#F5F5F5',
                  color: 'text.secondary',
                },
              },
            }}
          />
          <Typography variant='h6' sx={{ fontWeight: 600, fontSize: 16, marginBottom: 1 }}>
            Additional Comments (optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder='Brief summary of the issue or context...'
            value={additionalComments}
            onChange={(e) => setAdditionalComments(e.target.value)}
            size='small'
            sx={{ marginBottom: 2 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', justifyContent: 'center', gap: '8px' }}>
        <Button variant='outlined' disableElevation onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disableElevation
          color='primary'
          onClick={handleSend}
          disabled={!isFormValid || isSending}
        >
          {isSending ? 'Sending...' : 'Send report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SendErrorReportDialog;
